import { Dispatch, Fragment, ReactElement, SetStateAction } from 'react'
import { NavLink, Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { isMobileOnly as isMobile } from 'react-device-detect'
import {
  createStyles,
  makeStyles,
  useTheme,
  SwipeableDrawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Box,
  Typography,
  Theme
} from '@material-ui/core'
import {
  CheckCircle,
  RadioButtonUnchecked,
  HelpOutlineRounded,
  Settings
} from '@material-ui/icons'
import Urls from 'ustaxes/data/urls'
import { YearsTaxesState } from 'ustaxes/redux'
import { getSectionCompletions } from './completionStatus'

const drawerWidth = 260

const useStyles = makeStyles<Theme, { isMobile: boolean }>((theme) =>
  createStyles({
    drawer: {
      [theme.breakpoints.up('sm')]: {
        width: drawerWidth,
        flexShrink: 0
      }
    },
    drawerBackdrop: ({ isMobile }) => ({
      top: isMobile ? '68px !important' : undefined,
      height: isMobile ? 'calc(100% - 68px)' : undefined
    }),
    drawerContainer: ({ isMobile }) => ({
      top: isMobile ? '68px !important' : 0
    }),
    drawerPaper: ({ isMobile }) => ({
      top: isMobile ? '68px !important' : '72px',
      width: isMobile ? '100%' : drawerWidth,
      height: isMobile ? 'calc(100% - 68px)' : 'calc(100% - 72px)',
      backgroundColor:
        theme.palette.type === 'dark' ? '#1A1E3A' : '#FAFAFA',
      borderRight: '1px solid rgba(0,0,0,0.08)',
      overflowX: 'hidden'
    }),
    brandSection: {
      padding: theme.spacing(2.5, 2.5, 1.5),
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1)
    },
    brandIcon: {
      width: 32,
      height: 32,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 8,
      background: 'linear-gradient(135deg, #1A237E 0%, #283593 100%)',
      flexShrink: 0
    },
    brandText: {
      fontWeight: 800,
      fontSize: '1.1rem',
      background:
        theme.palette.type === 'dark'
          ? 'linear-gradient(135deg, #E8EAF6 0%, #EF5350 100%)'
          : 'linear-gradient(135deg, #1A237E 0%, #B71C1C 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text'
    },
    list: {
      padding: theme.spacing(0.5, 1)
    },
    sectionHeader: {
      fontSize: '0.7rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      color: theme.palette.text.secondary,
      lineHeight: '32px',
      paddingLeft: theme.spacing(2)
    },
    listItem: {
      borderRadius: 8,
      margin: '1px 0',
      padding: theme.spacing(0.75, 1.5),
      '&.Mui-selected': {
        backgroundColor:
          theme.palette.type === 'dark'
            ? 'rgba(92, 107, 192, 0.15)'
            : 'rgba(26, 35, 126, 0.06)',
        '&:hover': {
          backgroundColor:
            theme.palette.type === 'dark'
              ? 'rgba(92, 107, 192, 0.2)'
              : 'rgba(26, 35, 126, 0.08)'
        }
      },
      '&:hover': {
        backgroundColor:
          theme.palette.type === 'dark'
            ? 'rgba(255,255,255,0.04)'
            : 'rgba(26, 35, 126, 0.03)'
      }
    },
    listItemText: {
      '& .MuiListItemText-primary': {
        fontSize: '0.85rem',
        fontWeight: 500
      }
    },
    listItemTextSelected: {
      '& .MuiListItemText-primary': {
        fontSize: '0.85rem',
        fontWeight: 600,
        color: theme.palette.primary.main
      }
    },
    checkIcon: {
      minWidth: 28,
      '& .MuiSvgIcon-root': {
        fontSize: 16
      }
    },
    checkComplete: {
      color: '#2E7D32'
    },
    checkIncomplete: {
      color:
        theme.palette.type === 'dark'
          ? 'rgba(255,255,255,0.15)'
          : 'rgba(26, 35, 126, 0.15)'
    },
    divider: {
      margin: theme.spacing(0.5, 2),
      opacity: 0.5
    },
    bottomSection: {
      marginTop: 'auto',
      padding: theme.spacing(1, 1.5),
      borderTop:
        theme.palette.type === 'dark'
          ? '1px solid rgba(255,255,255,0.06)'
          : '1px solid rgba(26, 35, 126, 0.06)',
      display: 'flex',
      justifyContent: 'center',
      gap: theme.spacing(0.5)
    },
    bottomButton: {
      color: theme.palette.text.secondary,
      padding: 8,
      '&:hover': {
        color: theme.palette.primary.main,
        backgroundColor:
          theme.palette.type === 'dark'
            ? 'rgba(255,255,255,0.05)'
            : 'rgba(26, 35, 126, 0.04)'
      }
    }
  })
)

export interface Section {
  title: string
  items: SectionItem[]
}

export interface SectionItem {
  title: string
  url: string
  element: ReactElement
}

export const item = (
  title: string,
  url: string,
  element: ReactElement
): SectionItem => ({
  title,
  url,
  element
})

export interface DrawerItemsProps {
  sections: Section[]
  isOpen: boolean
  setOpen: Dispatch<SetStateAction<boolean>>
}

function ResponsiveDrawer(props: DrawerItemsProps): ReactElement {
  const classes = useStyles({ isMobile })
  const theme = useTheme()

  const { sections, isOpen, setOpen } = props

  const info = useSelector(
    (state: YearsTaxesState) => state[state.activeYear]
  )
  const completions = getSectionCompletions(info)

  const drawer = (
    <Box display="flex" flexDirection="column" height="100%">
      {/* Brand */}
      <Box className={classes.brandSection}>
        <span style={{ fontSize: 24, lineHeight: 1 }} role="img" aria-label="US flag">
          {'\u{1F1FA}\u{1F1F8}'}
        </span>
        <Typography className={classes.brandText}>Ameritax</Typography>
      </Box>

      {/* Navigation sections */}
      <Box style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {sections
          .filter((s) => s.title !== 'Ameritax')
          .map(({ title, items }) => {
            const completion = completions.find((c) => c.title === title)
            return (
              <Fragment key={`section ${title}`}>
                <List
                  subheader={
                    <ListSubheader
                      disableSticky
                      className={classes.sectionHeader}
                    >
                      {title}
                      {completion?.complete && (
                        <CheckCircle
                          style={{
                            fontSize: 12,
                            color: '#2E7D32',
                            marginLeft: 6,
                            verticalAlign: 'middle'
                          }}
                        />
                      )}
                    </ListSubheader>
                  }
                  className={classes.list}
                  dense
                >
                  {items.map((navItem) => {
                    const isSelected = location.pathname === navItem.url
                    return (
                      <ListItem
                        button
                        key={navItem.title}
                        component={NavLink}
                        selected={isSelected}
                        to={navItem.url}
                        className={classes.listItem}
                      >
                        <ListItemIcon className={classes.checkIcon}>
                          {completion?.complete ? (
                            <CheckCircle className={classes.checkComplete} />
                          ) : (
                            <RadioButtonUnchecked
                              className={classes.checkIncomplete}
                            />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={navItem.title}
                          className={
                            isSelected
                              ? classes.listItemTextSelected
                              : classes.listItemText
                          }
                        />
                      </ListItem>
                    )
                  })}
                </List>
              </Fragment>
            )
          })}
      </Box>

      {/* Bottom actions */}
      <Box className={classes.bottomSection}>
        <Link to={Urls.help}>
          <IconButton
            className={classes.bottomButton}
            aria-label="help"
            size="small"
          >
            <HelpOutlineRounded fontSize="small" />
          </IconButton>
        </Link>
        <Link to={Urls.settings}>
          <IconButton
            className={classes.bottomButton}
            aria-label="settings"
            size="small"
          >
            <Settings fontSize="small" />
          </IconButton>
        </Link>
      </Box>
    </Box>
  )

  return (
    <nav className={classes.drawer} aria-label="primary">
      <SwipeableDrawer
        variant={!isMobile ? 'persistent' : undefined}
        anchor={theme.direction === 'rtl' ? 'right' : 'left'}
        open={isOpen}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        classes={{
          root: classes.drawerContainer,
          paper: classes.drawerPaper
        }}
        ModalProps={{
          BackdropProps: {
            classes: { root: classes.drawerBackdrop }
          }
        }}
      >
        {drawer}
      </SwipeableDrawer>
    </nav>
  )
}

export default ResponsiveDrawer
